// The icon registry — plain data, no React and no imports.
//
// **The SVG in `assets/game-icons/` is the source; this is a copy of it.**
// Inlining is what lets an icon take `currentColor` (PLAN-UI §28.1) — an
// `<img>` cannot, and a sprite sheet is a request plus a build step. The copy
// is held to the source by `icons.test.ts`, the mirror guard `tokens.test.ts`
// holds over `kit-sheet.html`: two sources of truth is a drift waiting to
// happen, so the test reads the committed file and fails when the inlined path
// is not in it.
//
// **The background rect is stripped.** Every game-icons SVG opens with
// `<path d="M0 0h512v512H0z"/>` — a black plate behind white art. Inline that
// and the icon is a filled square, at every size, silently. The test asserts
// no entry carries one.
//
// Swapping the set is kit-internal: a call site names an icon, never a file.
// Attribution is per icon and also lives in `assets/LICENSES.md`; CC-BY 3.0
// wants the author named, so the author travels with the path.

export type IconName =
  | "pause"
  | "camera"
  | "track"
  | "lift"
  | "turret"
  | "moat"
  | "store"
  | "settings"
  | "forge"
  // **The six chess ranks are placeholders for commissioned piece art.** They
  // exist because the ♟♞♝♜♛♚ text glyphs are not durable: Font Awesome maps its
  // own icons onto U+265A–U+265F, so a machine with it installed as a system
  // font (Arch's `woff2-font-awesome`, say) draws every piece as a blank.
  // An inline path cannot be hijacked by a font.
  | "pawn"
  | "knight"
  | "bishop"
  | "rook"
  | "queen"
  | "king"
  | "close"
  | "chevronDown"
  | "chevronRight"
  // The turn row's own controls (§29 U4). Playback shapes, because that is
  // what history *is*: the transport reads as a video scrubber, Undo as the
  // circular arrow every application draws it as, and End turn as stop.
  | "undo"
  | "stop"
  | "skipPrevious"
  | "fastRewind"
  | "fastForward"
  | "skipNext"
  // The rail's camera group. Material again, for the reason the transport is:
  // these are chrome verbs every application draws the same way, and a
  // hand-drawn quarter-turn arrow would be the one control on the rail nobody
  // recognises on sight.
  | "rotateLeft"
  | "rotateRight"
  | "tiltShift"
  | "fitScreen";

export interface IconAsset {
  /** The source SVG's own viewBox — 512 for game-icons, 24 for Material. */
  viewBox: string;
  /** Path data, verbatim from the source. An array draws as several paths. */
  d: string | string[];
  /** Where it was copied from, and what `icons.test.ts` reads back. */
  source: string;
  /** Named because CC-BY 3.0 asks for it. Never empty. */
  author: string;
  licence: "CC-BY-3.0" | "Apache-2.0";
}

/**
 * game-icons.net entries carry a `source` relative to `assets/game-icons/`,
 * which is where the pack is committed — author directory and filename, so the
 * upstream path is readable straight off the entry.
 *
 * The Material entries are chrome and are **not** vendored: three arrowheads
 * are not worth a directory, and Apache-2.0 needs no file to travel with them.
 * Their `source` is a path in Google's `material-design-icons` repository.
 */
export const ICON_PATHS: Record<IconName, IconAsset> = {
  /** The pause button — the turn row's, and what Escape opens. */
  pause: {
    viewBox: "0 0 512 512",
    d: "M120.16 45A20.162 20.162 0 0 0 100 65.16v381.68A20.162 20.162 0 0 0 120.16 467h65.68A20.162 20.162 0 0 0 206 446.84V65.16A20.162 20.162 0 0 0 185.84 45h-65.68zm206 0A20.162 20.162 0 0 0 306 65.16v381.68A20.162 20.162 0 0 0 326.16 467h65.68A20.162 20.162 0 0 0 412 446.84V65.16A20.162 20.162 0 0 0 391.84 45h-65.68z",
    source: "guard13007/pause-button.svg",
    author: "Guard13007",
    licence: "CC-BY-3.0"
  },

  /** The Camera tool. */
  camera: {
    viewBox: "0 0 512 512",
    d: "M41 122.496v14h62v-14zm154.73 0l-32 32H137v46h30.682C192.4 159.898 237.08 132.738 288 132.738c50.92 0 95.6 27.16 120.318 67.758H487v-46h-74.73l-32-32c-92.27-9-92.27-9-184.54 0zM288 150.738c-67.903 0-122.758 54.855-122.758 122.758 0 67.903 54.855 122.758 122.758 122.758 67.903 0 122.758-54.855 122.758-122.758 0-67.903-54.855-122.758-122.758-122.758zm-263 3.758v46h94v-46zm263 14.713c57.49 0 104.287 46.796 104.287 104.286S345.49 377.783 288 377.783c-57.49 0-104.287-46.797-104.287-104.287 0-57.49 46.797-104.287 104.287-104.287zm-21.787 22.042c-12.173.42-25.717 6.526-36.78 16.578-20.025 18.19-26.342 43.853-14.11 57.318 12.232 13.465 38.38 9.634 58.406-8.558 20.023-18.192 26.34-43.855 14.108-57.32-5-5.504-12.62-8.33-21.625-8.018zM25 218.496v142h94v-142zm112 0v142h40.412c-18.888-23.96-30.17-54.183-30.17-87 0-19.507 3.988-38.096 11.188-55zm280.57 0c7.2 16.904 11.188 35.493 11.188 55 0 32.817-11.282 63.04-30.17 87H487v-142zM25 378.496v14h94v-14zm112 0v14h75.89c-6.567-4.158-12.763-8.846-18.536-14zm244.646 0c-5.773 5.154-11.97 9.842-18.535 14H487v-14z",
    source: "delapouite/photo-camera.svg",
    author: "Delapouite",
    licence: "CC-BY-3.0"
  },

  /** The Build tool — laying belt track. */
  track: {
    viewBox: "0 0 512 512",
    d: "M18 112v39h38v-39zm102 0v39h48v-39zm112 0v39h48v-39zm112 0v39h48v-39zm112 0v39h38v-39zM18 169v30h476v-30zm0 48v78h38v-78zm102 0v78h48v-78zm112 0v78h48v-78zm112 0v78h48v-78zm112 0v78h38v-78zM18 313v30h476v-30zm0 48v39h38v-39zm102 0v39h48v-39zm112 0v39h48v-39zm112 0v39h48v-39zm112 0v39h38v-39z",
    source: "delapouite/railway.svg",
    author: "Delapouite",
    licence: "CC-BY-3.0"
  },

  /** The Discard tool — taking track or a building of yours back off the board. */
  lift: {
    viewBox: "0 0 512 512",
    d: "M199 103v50h-78v30h270v-30h-78v-50H199zm18 18h78v32h-78v-32zm-79.002 80l30.106 286h175.794l30.104-286H137.998zm62.338 13.38l.64 8.98 16 224 .643 8.976-17.956 1.283-.64-8.98-16-224-.643-8.976 17.956-1.283zm111.328 0l17.955 1.284-.643 8.977-16 224-.64 8.98-17.956-1.284.643-8.977 16-224 .64-8.98zM247 215h18v242h-18V215z",
    source: "delapouite/trash-can.svg",
    author: "Delapouite",
    licence: "CC-BY-3.0"
  },

  /** The turret structure, and its licence row in Powers. */
  turret: {
    viewBox: "0 0 512 512",
    d: "M130.72 61C106 61.11 106 62.875 106 91H76l-15 30v60l15 30h30c0 30 0 30 30 30h120c30 0 30 0 30-30h15l15-30h180v-60H316l-15-30h-15c0-30 0-30-30-30H136c-1.875 0-3.633-.007-5.28 0zM166 271a15 15 0 0 0-15 15v45a15 15 0 0 0 15 15h60a15 15 0 0 0 15-15v-45a15 15 0 0 0-15-15h-60zm-15 105c-30 0-45 30-45 30-15 0-30 0-30 15l-60 15v15h360v-15l-60-15c0-15-15-15-30-15 0 0-15-30-45-30h-90z",
    source: "sbed/turret.svg",
    author: "Sbed",
    licence: "CC-BY-3.0"
  },

  /** The Static Moat structure. */
  moat: {
    viewBox: "0 0 512 512",
    d: "M400.9 104.8c-12 30-41 47.9-99.7 43.9-13.7-1.8-27.6-4.1-41.6-6.7-119.1-37.2-236.24-37.2-236.24 37.2 33.48-37.2 117.74-30.8 225.04-4 116.8 29.2 241.8 41.2 241.8-51.8-18.4 19.3-53.4 28.6-96.6 30.4 10-10.4 12.5-26.7 7.3-49zM147 187.5c-70.75-.3-123.64 16.1-123.64 66.1 33.48-37.2 117.74-34.8 225.04-8 116.8 29.2 241.8 45.2 241.8-47.8-35.4 37.2-130.2 39.6-230.6 8-37.7-11.9-78-18.2-112.6-18.3zm-23.9 69.6c-58.44-.2-99.74 15.6-99.74 70.9 33.48-37.2 122.34-44.3 225.04-18.6 121 30.2 241.8 37.2 241.8-37.2-35.4 37.2-132.1 22.6-230.6 4-48.4-7.5-96.5-19.1-136.5-19.1zm0 74.3c-58.44-.1-99.74 15.8-99.74 71 19.03-21.1 55.52-30.3 102.54-30.8-10.4 10.4-12.9 26.9-7.7 49.4 13.9-34.8 52-51.8 130.3-37.2 122.6 22.8 241.7 37.2 241.7-37.2-35.4 37.2-132.1 18.6-230.6 0-48.4-7.6-96.5-15.1-136.5-15.2z",
    source: "lorc/waves.svg",
    author: "Lorc",
    licence: "CC-BY-3.0"
  },

  /** The store structure, and holdings. */
  store: {
    viewBox: "0 0 512 512",
    d: "M256 73.32L74.6 155.1l82.3 37.1 173.2-85.5L256 73.32zm95.4 42.98l-173.2 85.5 77.8 35.1 181.4-81.8-86-38.8zM61.7 169v182L247 434.6v-182L61.7 169zm388.6 0L265 252.6v182L450.3 351V169z",
    source: "delapouite/cardboard-box-closed.svg",
    author: "Delapouite",
    licence: "CC-BY-3.0"
  },

  /** The pause menu's Settings screen. */
  settings: {
    viewBox: "0 0 512 512",
    d: "M234.875 18.78c-26.087 2.367-51.557 8.56-74.875 18.782 15.37 32.763 14.222 66.706-6.72 82.407-20.835 15.617-54.055 7.965-81.124-15.69-16.246 19.452-29.336 41.36-38.875 65.626 33.83 12.333 56.635 37.665 52.94 63.5-3.698 25.835-32.697 43.74-68.626 46.094 2.338 25.796 8.91 50.778 18.937 73.875 17.81-8.182 35.793-11.09 51.095-8.938 13.032 1.87 23.927 7.015 31.156 16.657 15.817 21.097 7.603 54.713-16.78 81.97 19.516 16.35 42.216 29.444 66.594 39.03 12.33-33.828 37.655-56.634 63.5-52.938 25.844 3.697 43.74 32.696 46.094 68.625 26.087-2.365 51.557-8.555 74.875-18.78-15.766-32.997-14.26-67.588 6.843-83.406 9.64-7.23 22.568-9.022 35.594-7.125 15.112 2.16 31.19 10.25 45.563 22.78 16.088-19.345 29.4-41.51 38.875-65.594-33.83-12.332-56.635-37.653-52.938-63.5 3.697-25.846 32.665-43.772 68.594-46.125-2.36-25.944-8.774-50.663-18.906-73.874-32.612 15.117-66.66 13.145-82.282-7.687-15.696-20.944-7.252-53.86 16.688-81-19.52-16.352-42.248-29.447-66.625-39.032-12.332 33.828-37.657 56.66-63.5 52.968-25.846-3.693-43.744-32.696-46.095-68.625zm21.656 95.126c79.626 0 144.376 64.752 144.376 144.375 0 79.626-64.75 144.376-144.375 144.376-79.624 0-144.374-64.75-144.374-144.375 0-79.624 64.75-144.374 144.375-144.374zm0 18.688c-69.524 0-125.686 56.162-125.686 125.687 0 69.526 56.162 125.69 125.687 125.69 69.526 0 125.69-56.164 125.69-125.69 0-69.522-56.164-125.686-125.69-125.686zm.033 15.125c61.094 0 110.625 49.53 110.625 110.624 0 61.095-49.53 110.625-110.625 110.625s-110.625-49.53-110.625-110.626c0-61.095 49.53-110.625 110.625-110.625z",
    source: "lorc/cog.svg",
    author: "Lorc",
    licence: "CC-BY-3.0"
  },

  /** Forging — the hotbar's mint tiles. */
  forge: {
    viewBox: "0 0 512 512",
    d: "M128.688 115.594v147.75h285v-147.75h-285zm-111.844 20.47c17.374 47.14 54.372 80.413 94.906 93.81v-93.81H16.844zm414.375 12.31v88.657c21.457-9.083 42.92-25.257 64.374-47.374-21.52-22.562-42.633-35.173-64.375-41.28zm-226.25 132.47c-12.15 38.536-33.897 71.5-60.595 100.47l257.844-.002c-28.705-29.016-49.952-62.054-61.5-100.468H204.97zM101.843 400v43.78h337.562V400H101.844z",
    source: "lorc/anvil.svg",
    author: "Lorc",
    licence: "CC-BY-3.0"
  },

  /** The pawn rank — the hotbar's mint tile, the inspector's rank tiles, the costs table. */
  pawn: {
    viewBox: "0 0 512 512",
    d: "M312.07 194.46A56.07 56.07 0 1 1 256 138.39a56.07 56.07 0 0 1 56.07 56.07zM406 418.01H106v60h300v-60zM282.33 261.52a71.81 71.81 0 0 1-52.15.2c-.73 58.91-62.35 114.06-96.75 140.28H378.9c-34.09-26.33-95.44-81.78-96.57-140.48z",
    source: "skoll/chess-pawn.svg",
    author: "Skoll",
    licence: "CC-BY-3.0"
  },

  /** The knight rank — the hotbar's mint tile, the inspector's rank tiles, the costs table. */
  knight: {
    viewBox: "0 0 512 512",
    d: "M60.81 476.91h300v-60h-300v60zm233.79-347.3l13.94 7.39c31.88-43.62 61.34-31.85 61.34-31.85l-21.62 53 35.64 19 2.87 33 64.42 108.75-43.55 29.37s-26.82-36.39-39.65-43.66c-10.66-6-41.22-10.25-56.17-12l-67.54-76.91-12 10.56 37.15 42.31c-.13.18-.25.37-.38.57-35.78 58.17 23 105.69 68.49 131.78H84.14C93 85 294.6 129.61 294.6 129.61z",
    source: "skoll/chess-knight.svg",
    author: "Skoll",
    licence: "CC-BY-3.0"
  },

  /** The bishop rank — the hotbar's mint tile, the inspector's rank tiles, the costs table. */
  bishop: {
    viewBox: "0 0 512 512",
    d: "M406.02 476.915h-300v-60h300v60zm-83.46-181H189.48v17.65h133.08v-17.65zm11.78-77.69a200 200 0 0 1-9.39 61.69H187.09a200 200 0 0 1-9.39-61.69c0-59.09 23.82-109 56.41-124.67a33.34 33.34 0 1 1 43.82 0c32.59 15.71 56.41 65.58 56.41 124.67zm-51.07-48.91h-19.25v-23.92h-16v23.92h-19.26v16h19.26v51.54h16v-51.54h19.25v-16zm38.15 180.69v-20.44h-130.8v20.44H93.29v.11l49.46 49.46h82.08l31.15-36 31.15 36h82.44l48.87-48.87.27-.69h-97.29z",
    source: "skoll/chess-bishop.svg",
    author: "Skoll",
    licence: "CC-BY-3.0"
  },

  /** The rook rank — the hotbar's mint tile, the inspector's rank tiles, the costs table. */
  rook: {
    viewBox: "0 0 512 512",
    d: "M406 484.7H106v-60h300v60zm-56.67-330.83h-50.05V91.3h-82.39v62.57h-54.22V91.3h-54.23v113.67h295.12V91.3h-54.23v62.57zm23.35 67.23H139.32v187.6h233.36V221.1z",
    source: "skoll/chess-rook.svg",
    author: "Skoll",
    licence: "CC-BY-3.0"
  },

  /** The queen rank — the hotbar's mint tile, the inspector's rank tiles, the costs table. */
  queen: {
    viewBox: "0 0 512 512",
    d: "M477.518 181.966a25 25 0 0 1-34.91 23l-62.29 150.26h-248.92l-62.24-150.19a25 25 0 1 1 9.73-7.29l87 71.2 20.92-126.4a25 25 0 1 1 14.7-1.85l54.31 117 54.42-117.3a25 25 0 1 1 14.58 2.08l20.93 126.42 87.26-71.3a25 25 0 1 1 44.51-15.63zm-71.66 241.25h-300v60h300v-60zm-27.75-52h-244.22v36h244.22v-36z",
    source: "skoll/chess-queen.svg",
    author: "Skoll",
    licence: "CC-BY-3.0"
  },

  /** The king rank — the hotbar's mint tile, the inspector's rank tiles, the costs table. */
  king: {
    viewBox: "0 0 512 512",
    d: "M405.995 477.15h-300v-60h300v60zm-10.3-107.13h-279.4a96.88 96.88 0 0 1 6.65 31.12h266.1a96.88 96.88 0 0 1 6.65-31.12zm-139.7-241.06a35.76 35.76 0 0 0-35.76 35.76c0 50.16 35.76 99.34 35.76 99.34s35.76-49.18 35.76-99.34a35.76 35.76 0 0 0-35.76-35.76zm8-15.38V94.24h18.36v-16h-18.36V54.85h-16v23.39h-18.36v16h18.36v19.38a51.9 51.9 0 0 1 16-.04zm81.64 51.36a98.74 98.74 0 0 0-38.13 7.61c-3.23 51.75-37.07 98.85-38.58 100.93l-4.93 6.76V354h140c16.57-26.15 40.78-42.41 40.78-90a99.13 99.13 0 0 0-99.14-99.07zm-141.16 7.61a99.16 99.16 0 0 0-137.25 91.51c0 47.55 24.21 63.82 40.78 90h139.99v-73.82l-4.94-6.79c-1.51-2.05-35.34-49.15-38.58-100.9z",
    source: "skoll/chess-king.svg",
    author: "Skoll",
    licence: "CC-BY-3.0"
  },

  /** Dismissing a modal, popover or pill. */
  close: {
    viewBox: "0 0 24 24",
    d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
    source: "src/navigation/close/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** A readout or section that expands downward. */
  chevronDown: {
    viewBox: "0 0 24 24",
    d: "M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z",
    source: "src/navigation/expand_more/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** A row that opens a flyout — the build category tile's arrow. */
  chevronRight: {
    viewBox: "0 0 24 24",
    d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z",
    source: "src/navigation/chevron_right/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** Take back the last action of this turn — the turn row's Undo (§29 U2). */
  undo: {
    viewBox: "0 0 24 24",
    d: "M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z",
    source: "src/client/ai/content/undo/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /**
   * End turn — the playback *stop*, not a hand or a flag.
   *
   * The row it sits in is a transport (§29 U4), so the one button that ends
   * the thing being played is the one shape a transport already has for it.
   */
  stop: {
    viewBox: "0 0 24 24",
    d: "M6 6h12v12H6z",
    source: "src/av/stop/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** History: jump to the opening position. */
  skipPrevious: {
    viewBox: "0 0 24 24",
    d: "M6 6h2v12H6zm3.5 6l8.5 6V6z",
    source: "src/av/skip_previous/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** History: one step back. */
  fastRewind: {
    viewBox: "0 0 24 24",
    d: "M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z",
    source: "src/av/fast_rewind/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** History: one step forward. */
  fastForward: {
    viewBox: "0 0 24 24",
    d: "M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z",
    source: "src/av/fast_forward/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** History: back to the live board, where you can play. */
  skipNext: {
    viewBox: "0 0 24 24",
    d: "M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z",
    source: "src/av/skip_next/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** The rail: a quarter turn anticlockwise. */
  rotateLeft: {
    viewBox: "0 0 24 24",
    d: "M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47zM6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47zm1.01 5.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32zM13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z",
    source: "src/image/rotate_left/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** The rail: a quarter turn clockwise. */
  rotateRight: {
    viewBox: "0 0 24 24",
    d: "M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zM13 17.9v2.02c1.39-.17 2.74-.71 3.9-1.61l-1.44-1.44c-.75.54-1.59.89-2.46 1.03zm3.89-2.42l1.42 1.41c.9-1.16 1.45-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.48z",
    source: "src/image/rotate_right/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /**
   * The rail: table view or straight down.
   *
   * A ring seen flat with a dot in the middle, which is what looking straight
   * down at the board *is*. `3d_rotation` was the other candidate and says
   * "turn", which the two tiles beside it already say — two rotation icons in
   * one group of four is the confusion, not the shortcut.
   */
  tiltShift: {
    viewBox: "0 0 24 24",
    d: "M11 4.07V2.05c-2.01.2-3.84 1-5.32 2.21L7.1 5.69c1.11-.86 2.44-1.44 3.9-1.62zm7.32.19C16.84 3.05 15.01 2.25 13 2.05v2.02c1.46.18 2.79.76 3.9 1.62l1.42-1.43zM19.93 11h2.02c-.2-2.01-1-3.84-2.21-5.32L18.31 7.1c.86 1.11 1.44 2.44 1.62 3.9zM5.69 7.1L4.26 5.68C3.05 7.16 2.25 8.99 2.05 11h2.02c.18-1.46.76-2.79 1.62-3.9zM4.07 13H2.05c.2 2.01 1 3.84 2.21 5.32l1.43-1.43c-.86-1.1-1.44-2.43-1.62-3.89zM15 12c0-1.66-1.34-3-3-3s-3 1.34-3 3 1.34 3 3 3 3-1.34 3-3zm3.31 4.9l1.43 1.43c1.21-1.48 2.01-3.32 2.21-5.32h-2.02c-.18 1.45-.76 2.78-1.62 3.89zM13 19.93v2.02c2.01-.2 3.84-1 5.32-2.21l-1.43-1.43c-1.1.86-2.43 1.44-3.89 1.62zm-7.32-.19C7.16 20.95 9 21.75 11 21.95v-2.02c-1.46-.18-2.79-.76-3.9-1.62l-1.42 1.43z",
    source: "src/image/filter_tilt_shift/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  },

  /** The rail: pull back until every square is on screen. */
  fitScreen: {
    viewBox: "0 0 24 24",
    d: "M17 4h3c1.1 0 2 .9 2 2v2h-2V6h-3V4zM4 8V6h3V4H4c-1.1 0-2 .9-2 2v2h2zm16 8v2h-3v2h3c1.1 0 2-.9 2-2v-2h-2zM7 18H4v-2H2v2c0 1.1.9 2 2 2h3v-2zM18 8H6v8h12V8z",
    source: "src/action/fit_screen/materialicons/24px.svg",
    author: "Google",
    licence: "Apache-2.0"
  }
};
